import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    cellPhone: '',
    optionalCode: ''  // ✅ ADDED: Optional code for tracking
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData({ ...formData, cellPhone: formatted });
    setError('');
  };

  // ✅ ADDED: Format optional code (uppercase, alphanumeric + hyphens only)
  const formatOptionalCode = (value) => {
    // Remove invalid characters, allow only letters, numbers, and hyphens
    const cleaned = value.replace(/[^A-Za-z0-9-]/g, '');
    // Convert to uppercase and limit to 20 characters
    return cleaned.toUpperCase().slice(0, 20);
  };

  // ✅ ADDED: Handle optional code changes with formatting
  const handleOptionalCodeChange = (e) => {
    const formatted = formatOptionalCode(e.target.value);
    setFormData({ ...formData, optionalCode: formatted });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/register`,
        formData  // ✅ INCLUDES: optionalCode in submission
      );
      
      // Store email for verification
      localStorage.setItem('pendingVerificationEmail', formData.email);
      localStorage.setItem('authToken', response.data.token);
      
      // Show verification form
      setShowVerification(true);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/verify-phone`,
        {
          email: formData.email,
          code: verificationCode
        }
      );
      
      console.log("🔍 VERIFICATION RESPONSE:", response.data);
      console.log("🔍 USER ID:", response.data.user?.id);
      console.log("🔍 PLAN PARAM:", new URLSearchParams(location.search).get("plan"));
      if (response.data.success) {
        // Verification successful, redirect based on plan
        const searchParams = new URLSearchParams(location.search);
        const plan = searchParams.get('plan');
        
        if (plan === 'monthly') {
          // Redirect to monthly Stripe checkout with userId
          window.location.href = `https://buy.stripe.com/bJe4gA2K22VNe2Odst3Je05?client_reference_id=${formData.email}`;
        } else if (plan === 'yearly') {
          // Redirect to yearly Stripe checkout with userId
          window.location.href = `https://buy.stripe.com/9B64gA1FY53VaQC7453Je04?client_reference_id=${formData.email}`;
        } else {
          // No plan specified, go to dashboard (free trial flow)
          navigate('/dashboard');
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/resend-verification`,
        { email: formData.email }
      );
      
      if (response.data.success) {
        setError('New verification code sent!');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  // Registration Form
  if (!showVerification) {
    return (
      <div style={{maxWidth: '400px', margin: '3rem auto', padding: '2rem', background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
        <h2 style={{textAlign: 'center', color: '#2e7d32'}}>Start Your Free Trial</h2>
        <p style={{textAlign: 'center', color: '#666', marginBottom: '2rem'}}>
          250,000 tokens to explore your family history with Gene
        </p>
        
        {error && (
          <div style={{
            background: '#f8d7da',
            color: '#721c24',
            padding: '0.75rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
          <div>
            <label style={{display: 'block', marginBottom: '0.5rem'}}>Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Enter your full name"
              style={{width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px'}}
            />
          </div>
          
          <div>
            <label style={{display: 'block', marginBottom: '0.5rem'}}>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter your email"
              style={{width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px'}}
            />
          </div>
          
          <div>
            <label style={{display: 'block', marginBottom: '0.5rem'}}>Phone Number</label>
            <input
              type="tel"
              name="cellPhone"
              value={formData.cellPhone}
              onChange={handlePhoneChange}
              required
              placeholder="(555) 123-4567"
              maxLength="14"
              style={{width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px'}}
            />
            <small style={{display: 'block', marginTop: '0.25rem', color: '#666', fontSize: '0.875rem'}}>
              We'll send a verification code to this number
            </small>
          </div>

          {/* ✅ ADDED: Optional Code Field */}
          <div>
            <label style={{display: 'block', marginBottom: '0.5rem'}}>
              Optional Code 
              <span style={{color: '#999', fontWeight: 'normal'}}> (Promo)</span>
            </label>
            <input
              type="text"
              name="optionalCode"
              value={formData.optionalCode}
              onChange={handleOptionalCodeChange}
              placeholder="Enter promo/referral code"
              maxLength="20"
              style={{
                width: '100%', 
                padding: '0.5rem', 
                border: '1px solid #ddd', 
                borderRadius: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            />
            <small style={{display: 'block', marginTop: '0.25rem', color: '#666', fontSize: '0.875rem'}}>
              💡 Have a promo code?<br/>Enter it here
            </small>
          </div>
          
          <div>
            <label style={{display: 'block', marginBottom: '0.5rem'}}>Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="Create a password"
              style={{width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px'}}
            />
          </div>
          
          <button 
            type="submit" 
            disabled={loading}
            style={{
              background: loading ? '#ccc' : '#2e7d32',
              color: 'white',
              border: 'none',
              padding: '0.75rem',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              marginTop: '0.5rem'
            }}
          >
            {loading ? 'Creating Account...' : 'Start Free Trial'}
          </button>
        </form>
        
        <p style={{textAlign: 'center', marginTop: '1.5rem', color: '#666'}}>
          Already have an account? <Link to="/login" style={{color: '#2e7d32'}}>Sign in</Link>
        </p>
      </div>
    );
  }

  // Verification Form (unchanged)
  return (
    <div style={{maxWidth: '400px', margin: '3rem auto', padding: '2rem', background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
      <h2 style={{textAlign: 'center', color: '#2e7d32'}}>Verify Your Phone Number</h2>
      <p style={{textAlign: 'center', color: '#666', marginBottom: '2rem'}}>
        We sent a 6-digit code to {formData.cellPhone}
      </p>
      
      {error && (
        <div style={{
          background: error.includes('sent') ? '#d4edda' : '#f8d7da',
          color: error.includes('sent') ? '#155724' : '#721c24',
          padding: '0.75rem',
          borderRadius: '4px',
          marginBottom: '1rem',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}
      
      <form onSubmit={handleVerification} style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
        <div>
          <label style={{display: 'block', marginBottom: '0.5rem'}}>Verification Code</label>
          <input
            type="text"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            required
            placeholder="123456"
            maxLength="6"
            autoFocus
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '1.5rem',
              textAlign: 'center',
              letterSpacing: '0.5rem'
            }}
          />
        </div>
        
        <button 
          type="submit" 
          disabled={loading}
          style={{
            background: loading ? '#ccc' : '#2e7d32',
            color: 'white',
            border: 'none',
            padding: '0.75rem',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '1rem'
          }}
        >
          {loading ? 'Verifying...' : 'Verify Phone Number'}
        </button>
      </form>
      
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: '1.5rem',
        gap: '1rem'
      }}>
        <button 
          onClick={handleResendCode} 
          disabled={loading}
          style={{
            background: 'none',
            border: 'none',
            color: '#2e7d32',
            cursor: loading ? 'not-allowed' : 'pointer',
            textDecoration: 'underline',
            fontSize: '0.9rem',
            padding: '0'
          }}
        >
          Resend Code
        </button>
        <span style={{color: '#ccc'}}>•</span>
        <button 
          onClick={() => setShowVerification(false)}
          style={{
            background: 'none',
            border: 'none',
            color: '#2e7d32',
            cursor: 'pointer',
            textDecoration: 'underline',
            fontSize: '0.9rem',
            padding: '0'
          }}
        >
          Change Phone Number
        </button>
      </div>
      
      <p style={{textAlign: 'center', marginTop: '1.5rem', color: '#666'}}>
        <Link to="/login" style={{color: '#2e7d32'}}>Back to Login</Link>
      </p>
    </div>
  );
};

export default RegisterPage;
